import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Wallet, Phone, MessageSquare, AlertTriangle, Loader2, Plus } from 'lucide-react';

const PRESETS = [2500, 5000, 10000, 25000]; // cents: $25 / $50 / $100 / $250

const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function CreditsPage() {
  const { api } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(10000); // default $100 (Ali's example)
  const [custom, setCustom] = useState('');
  const [buying, setBuying] = useState(false);
  const [ar, setAr] = useState(null); // auto-reload draft: {enabled, threshold, amount} in dollars
  const [arSaving, setArSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/credits/balance');
      setData(data);
    } catch {
      toast.error('Could not load your credit balance.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Seed the auto-reload form from the server the first time data arrives.
  useEffect(() => {
    if (data && ar === null) {
      const a = data.auto_reload || {};
      setAr({
        enabled: !!a.enabled,
        threshold: ((a.threshold_cents ?? data.low_balance_threshold_cents ?? 1000) / 100),
        amount: ((a.amount_cents ?? 5000) / 100),
      });
    }
  }, [data, ar]);

  const saveAutoReload = async (next) => {
    setArSaving(true);
    try {
      const body = {
        enabled: next.enabled,
        threshold_cents: Math.round((parseFloat(next.threshold) || 10) * 100),
        amount_cents: Math.round((parseFloat(next.amount) || 50) * 100),
      };
      const { data: res } = await api.post('/credits/auto-reload', body);
      setAr({
        enabled: res.auto_reload.enabled,
        threshold: res.auto_reload.threshold_cents / 100,
        amount: res.auto_reload.amount_cents / 100,
      });
      toast.success(next.enabled ? 'Auto-reload on.' : 'Auto-reload off.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save auto-reload.');
    } finally {
      setArSaving(false);
    }
  };

  // Handle the Stripe return (?topup=success|cancelled).
  useEffect(() => {
    const t = params.get('topup');
    if (!t) return;
    if (t === 'success') toast.success('Credits added — thank you!');
    if (t === 'cancelled') toast('Top-up cancelled.');
    params.delete('topup'); params.delete('amount');
    setParams(params, { replace: true });
    // Stripe webhook may land a moment after redirect — refresh a couple of times.
    load();
    const tid = setTimeout(load, 3000);
    return () => clearTimeout(tid);
  }, [params, setParams, load]);

  const chosen = custom ? Math.round(parseFloat(custom) * 100) : amount;

  const buy = async () => {
    if (!chosen || chosen < (data?.min_topup_cents || 2000)) {
      toast.error(`Minimum top-up is ${fmt(data?.min_topup_cents || 2000)}.`);
      return;
    }
    setBuying(true);
    try {
      const { data: res } = await api.post('/credits/checkout', { amount_cents: chosen });
      if (res?.url) window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const balance = data?.balance_cents || 0;
  const threshold = data?.low_balance_threshold_cents || 1000;
  const isLow = data?.is_low;
  const isDepleted = data?.is_depleted;
  const rateMin = data?.rate_per_min_cents || 30;
  const rateSms = data?.rate_per_sms_cents || 5;
  const ledger = data?.ledger || [];

  return (
    <div data-testid="credits-page" className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Wallet className="w-6 h-6 text-blue-500 dark:text-blue-400" />
        <h1 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white">Credits</h1>
      </div>

      {(isDepleted || isLow) && (
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${
          isDepleted
            ? 'bg-red-50 dark:bg-red-500/15 border-red-200 dark:border-red-500/30'
            : 'bg-amber-50 dark:bg-amber-500/15 border-amber-200 dark:border-amber-500/40'
        }`}>
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${isDepleted ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
          <p className={`text-sm font-medium ${isDepleted ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
            {isDepleted
              ? 'Your credits are used up. Top up to keep Ava answering calls and texts.'
              : `Your balance is low (under ${fmt(threshold)}). Top up to avoid interruptions.`}
          </p>
        </div>
      )}

      {/* Balance + top-up */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Balance card */}
        <div className="rounded-2xl p-6 bg-white border border-gray-200 dark:bg-white/[0.04] dark:border-white/10">
          <p className="text-sm text-gray-500 dark:text-slate-400">Current balance</p>
          <p className={`mt-1 text-4xl font-semibold ${isDepleted ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
            {fmt(balance)}
          </p>
          <div className="mt-5 space-y-3">
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-300">
              <Phone className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <span>{fmt(rateMin)} / voice minute</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-300">
              <MessageSquare className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <span>{fmt(rateSms)} / text message</span>
            </div>
          </div>
          {data?.last_topup_at && (
            <p className="mt-5 text-xs text-gray-400 dark:text-slate-500">
              Last top-up {fmtDate(data.last_topup_at)} · Total used {fmt(data?.credits_used_cents)}
            </p>
          )}
        </div>

        {/* Top-up card */}
        <div className="rounded-2xl p-6 bg-white border border-gray-200 dark:bg-white/[0.04] dark:border-white/10">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Load credits</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => { setAmount(p); setCustom(''); }}
                className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${
                  !custom && amount === p
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-transparent text-gray-700 dark:text-slate-200 border-gray-200 dark:border-white/10 hover:border-blue-400'
                }`}
              >
                {fmt(p)}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label className="text-xs text-gray-500 dark:text-slate-400">Custom amount</label>
            <div className="mt-1 flex items-center rounded-lg border border-gray-200 dark:border-white/10 px-3 bg-transparent">
              <span className="text-gray-400">$</span>
              <input
                type="number" min="20" inputMode="decimal" placeholder="100"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="w-full bg-transparent py-2.5 px-2 text-sm text-gray-900 dark:text-white outline-none"
              />
            </div>
          </div>
          <button
            onClick={buy}
            disabled={buying}
            className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add {fmt(chosen)} in credits
          </button>
          <p className="mt-3 text-xs text-gray-400 dark:text-slate-500">
            Pay-as-you-go credits are non-refundable once added. You're only charged for what Ava uses.
          </p>
        </div>
      </div>

      {/* Auto-reload */}
      {ar && (
        <div className="rounded-2xl p-6 bg-white border border-gray-200 dark:bg-white/[0.04] dark:border-white/10">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Auto-reload</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                Automatically charge your saved card to keep Ava running — no interruptions.
              </p>
            </div>
            <button
              role="switch" aria-checked={ar.enabled} disabled={arSaving}
              onClick={() => saveAutoReload({ ...ar, enabled: !ar.enabled })}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${ar.enabled ? 'bg-blue-500' : 'bg-gray-200 dark:bg-white/15'}`}>
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${ar.enabled ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {ar.enabled && (
            <div className="mt-5 flex flex-wrap items-end gap-4">
              <label className="text-xs text-gray-500 dark:text-slate-400">
                When balance falls below
                <div className="mt-1 flex items-center rounded-lg border border-gray-200 dark:border-white/10 px-3">
                  <span className="text-gray-400">$</span>
                  <input type="number" min="0" value={ar.threshold}
                    onChange={(e) => setAr({ ...ar, threshold: e.target.value })}
                    className="w-20 bg-transparent py-2 px-1.5 text-sm text-gray-900 dark:text-white outline-none" />
                </div>
              </label>
              <label className="text-xs text-gray-500 dark:text-slate-400">
                Top up by
                <div className="mt-1 flex items-center rounded-lg border border-gray-200 dark:border-white/10 px-3">
                  <span className="text-gray-400">$</span>
                  <input type="number" min="20" value={ar.amount}
                    onChange={(e) => setAr({ ...ar, amount: e.target.value })}
                    className="w-20 bg-transparent py-2 px-1.5 text-sm text-gray-900 dark:text-white outline-none" />
                </div>
              </label>
              <button onClick={() => saveAutoReload(ar)} disabled={arSaving}
                className="py-2.5 px-5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium disabled:opacity-60">
                {arSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Ledger */}
      <div className="rounded-2xl bg-white border border-gray-200 dark:bg-white/[0.04] dark:border-white/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-white/10">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Activity</p>
        </div>
        {ledger.length === 0 ? (
          <p className="px-6 py-8 text-sm text-gray-400 dark:text-slate-500 text-center">
            No activity yet. Load credits to get started.
          </p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-white/[0.06]">
            {ledger.map((e, i) => (
              <div key={i} className="px-6 py-3 flex items-center justify-between text-sm">
                <div>
                  <p className="text-gray-800 dark:text-slate-200">
                    {e.kind === 'topup' ? 'Top-up' : e.detail || e.kind}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-slate-500">{fmtDate(e.ts)}</p>
                </div>
                <div className="text-right">
                  <p className={`font-medium ${e.amount_cents >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-slate-300'}`}>
                    {e.amount_cents >= 0 ? '+' : '−'}{fmt(Math.abs(e.amount_cents))}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-slate-500">{fmt(e.balance_cents)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
